import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { ResultsAppendedEvent } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 * Pages are appended one at a time when the load more button is clicked.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [loadMore] - The load more button wrapper.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    
    requestAnimationFrame(() => {
      this.#sortCards();
    });

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchNextPage();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Appends the next page of results. Bound to the load more button.
   */
  async loadMore() {
    const { loadMore } = this.refs;

    loadMore?.setAttribute('aria-busy', 'true');

    try {
      await this.#renderNextPage();
    } finally {
      loadMore?.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  async #fetchNextPage() {
    const page = this.#getNextPage();

    // Always resolve the promise, even if we can't fetch the page
    if (!page || !this.#shouldUsePage(page)) {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);

    this.#resolveNextPagePromise?.();
    this.#resolveNextPagePromise = null;
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getNextPage();

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchNextPage();

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#sortCards();
    this.dispatchEvent(new ResultsAppendedEvent(nextPageItemElements));

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    // The address bar keeps pointing at the first page: appended pages stack on top of the
    // existing ones, so a URL carrying ?page=N would drop everything above N on reload.
    this.#updateLoadMoreVisibility();

    requestIdleCallback(() => {
      this.#fetchNextPage();
    });
  }

  /**
   * Hides the load more button once the last page is on screen.
   */
  #updateLoadMoreVisibility() {
    const { loadMore } = this.refs;

    if (!loadMore) return;

    loadMore.hidden = !this.#shouldUsePage(this.#getNextPage());
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    const { cards } = this.refs;

    if (!Array.isArray(cards) || cards.length === 0) return;

    const pageNumbers = cards
      .map((card) => Number(card.dataset.page))
      .filter((page) => Number.isFinite(page));

    if (pageNumbers.length === 0) return;

    const page = Math.max(...pageNumbers) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }
#sortCards() {
    const { grid } = this.refs;

    if (!grid) return;

    const cards = [...grid.querySelectorAll(':scope > [ref="cards[]"]')];

    cards.sort((a, b) => {
      const aProduct = a.querySelector('product-card');
      const bProduct = b.querySelector('product-card');

      const aAvailable = aProduct?.dataset.productAvailable === 'true';
      const bAvailable = bProduct?.dataset.productAvailable === 'true';

      return Number(bAvailable) - Number(aAvailable);
    });

    grid.replaceChildren(...cards);
  }
  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolveNextPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#fetchNextPage();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
